/**
 * Quaternion plumbing between the rotate gizmo's proxy object and the bone it drives.
 *
 * The gizmo is never attached to a bone directly — it drives a proxy `Object3D` that lives in the
 * editor-only overlay group, and we decide what gets written back. That indirection is the whole
 * reason this file exists: an imported rig's bones may have arbitrary local axes and arbitrary
 * parent chains, so the only sound way to convert "the user rotated this handle" into "this bone's
 * local quaternion" is to go through world space.
 *
 * Everything here is pure three.js math — no DOM, no WebGL — so it is unit-testable.
 */

import { Quaternion } from 'three';
import type { Object3D } from 'three';

import type { QuaternionTuple } from '../../shared';

const _parentWorld = new Quaternion();
const _local = new Quaternion();

/**
 * Convert a proxy's world rotation into the local rotation the bone must carry.
 *
 * `local = parentWorld⁻¹ * proxyWorld`, normalized, and flipped into the same hemisphere as
 * `currentLocal` when one is supplied. The hemisphere fix matters: `q` and `-q` are the same
 * rotation, but a sign flip mid-drag makes anything diffing raw quaternions (keyframe dirty
 * checks, slerp between neighbouring keys) take the long way round.
 */
export function localFromProxyWorld(
  proxyWorld: Quaternion,
  parentWorld: Quaternion | null,
  currentLocal?: Quaternion | null,
  out: Quaternion = new Quaternion(),
): Quaternion {
  if (parentWorld) {
    out.copy(parentWorld).invert().multiply(proxyWorld);
  } else {
    out.copy(proxyWorld);
  }
  out.normalize();

  if (currentLocal) {
    const d =
      currentLocal.x * out.x + currentLocal.y * out.y + currentLocal.z * out.z + currentLocal.w * out.w;
    if (d < 0) out.set(-out.x, -out.y, -out.z, -out.w);
  }
  return out;
}

/**
 * Apply a proxy's world rotation to a live bone.
 *
 * Reads the bone's parent world rotation from the current scene graph, so the caller must have
 * brought the model's world matrices up to date this frame (the pose driver does that before the
 * gizmo layer runs). Returns the local quaternion that was written.
 */
export function writeProxyRotationToBone(bone: Object3D, proxyWorld: Quaternion): QuaternionTuple {
  const parentWorld = bone.parent ? bone.parent.getWorldQuaternion(_parentWorld) : null;
  // Note the scratch: `out` must not alias `currentLocal`, or the hemisphere comparison would
  // read the value we just overwrote.
  const local = localFromProxyWorld(proxyWorld, parentWorld, bone.quaternion, _local);
  bone.quaternion.copy(local);
  return [local.x, local.y, local.z, local.w];
}

/**
 * Point a proxy at a bone's current world transform (position + rotation, never scale).
 *
 * The proxy is parented to the identity overlay group, so its *local* transform is its world
 * transform; writing world values straight onto `position`/`quaternion` is correct there and
 * avoids a matrix decompose per frame.
 */
export function syncProxyToBone(proxy: Object3D, bone: Object3D): void {
  bone.updateWorldMatrix(true, false);
  const e = bone.matrixWorld.elements;
  proxy.position.set(e[12], e[13], e[14]);
  bone.getWorldQuaternion(proxy.quaternion);
  proxy.scale.set(1, 1, 1);
  proxy.updateMatrixWorld(true);
}
